import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/client";
import useAuthStore from "../store/authStore";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";

export default function EmployerNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { employerUser, clearEmployerAuth } = useAuthStore();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchCount = () =>
      api
        .get("/employer/notifications/unread-count")
        .then(({ data }) => setUnreadCount(data.count))
        .catch(() => {});

    fetchCount();
    const interval = setInterval(fetchCount, 30_000);
    return () => clearInterval(interval);
  }, []);

  const toggleNotifications = () => {
    const opening = !notifOpen;
    setNotifOpen(opening);
    if (opening) {
      setNotifLoading(true);
      api
        .get("/employer/notifications")
        .then(({ data }) => setNotifications(data))
        .catch(() => setNotifications([]))
        .finally(() => setNotifLoading(false));
      // Mark viewed immediately on open, matching how the seeker inbox
      // bell behaves — the badge should clear the moment the list is seen,
      // not only after each item is individually read.
      api.patch("/employer/notifications/mark-viewed").then(() => setUnreadCount(0));
    }
  };

  const formatRelative = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return new Date(iso).toLocaleDateString("en-SE", { day: "numeric", month: "short" });
  };

  const handleLogout = async () => {
    setDropdownOpen(false);
    try {
      await api.post("/employer/auth/logout");
    } catch {
      // Best-effort server logout; clear local state regardless.
    }
    clearEmployerAuth();
    navigate("/employers/login");
  };

  const isActive = (path) =>
    location.pathname === path ||
    (path !== "/employers/dashboard" && location.pathname.startsWith(path));

  // Company initials for the avatar circle.
  const initials = employerUser?.company_name
    ? employerUser.company_name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "E";

  // Canonical nav items in fixed order. Candidates only for agencies.
  const navItems = [
    { label: "Dashboard", path: "/employers/dashboard" },
    ...(employerUser?.is_agency
      ? [{ label: "Candidates", path: "/employers/candidates" }]
      : []),
    { label: "Positions", path: "/employers/positions" },
    { label: "Arena", path: "/employers/arena" },
    { label: "Settings", path: "/employers/settings" },
  ];

  return (
    <nav className="bg-white dark:bg-brand-dark-surface border-b border-gray-200 dark:border-white/10 px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 z-30">
      <div className="flex items-center gap-3">
        <Logo className="h-10 sm:h-12 flex-shrink-0" />
        <span className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark bg-brand-violet-tint dark:bg-brand-violet/20 px-2 py-0.5 rounded-full">
          {employerUser?.is_agency ? "Agency" : "Employer"}
        </span>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={toggleNotifications}
            className="relative p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-brand-violet text-white text-xs rounded-full flex items-center justify-center font-medium">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white dark:bg-brand-dark-surface rounded-2xl shadow-lg border border-gray-100 dark:border-white/10 z-50">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-white/10">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Notifications
                </p>
              </div>

              {notifLoading ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  Loading...
                </p>
              ) : notifications.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  Nothing yet.
                </p>
              ) : (
                <div className="py-1">
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => {
                        // Items without a position (unlinked pitches) have
                        // nowhere to go, so they stay plain text.
                        if (!n.position_id) return;
                        setNotifOpen(false);
                        navigate(`/employers/positions/${n.position_id}`);
                      }}
                      className={`px-4 py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 ${
                        n.position_id
                          ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition"
                          : ""
                      }`}
                    >
                      {n.type === "application" ? (
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {n.candidate_name ?? "A candidate"}
                          </span>{" "}
                          applied to{" "}
                          <span className="font-medium">
                            {n.position_title}
                          </span>
                        </p>
                      ) : (
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {n.candidate_name ?? "A candidate"}
                          </span>{" "}
                          {n.response === "accepted" || n.response === "submitted"
                            ? "accepted"
                            : n.response === "declined"
                              ? "declined"
                              : "responded to"}{" "}
                          {n.action_title ? `"${n.action_title}"` : "your update"}{" "}
                          for{" "}
                          <span className="font-medium">
                            {n.position_title}
                          </span>
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatRelative(n.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <ThemeToggle />

        {/* Profile dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen((o) => !o)}
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition"
          >
            <div className="w-8 h-8 rounded-full bg-brand-violet flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
              {initials}
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-300 hidden sm:block max-w-32 truncate">
              {employerUser?.company_name ?? "My Account"}
            </span>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-brand-dark-surface rounded-2xl shadow-lg border border-gray-100 dark:border-white/10 overflow-hidden z-50">
              {/* Company info */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-white/10">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {employerUser?.company_name ?? "My Account"}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {employerUser?.email ?? ""}
                </p>
              </div>

              {/* Nav items */}
              <div className="py-1.5">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => {
                      setDropdownOpen(false);
                      navigate(item.path);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition ${
                      isActive(item.path)
                        ? "bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark font-medium"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 dark:border-white/10 py-1.5">
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
